<template>
  <div class="bom-sidebar column full-height" style="position: relative">
    <!-- Expand Trigger Button -->
    <div class="expand-trigger" @click="showManagementDialog = true">
      <div class="expand-text">EXPAND TO MANAGE</div>
      <q-icon name="open_in_full" size="12px" />
    </div>

    <!-- Summary Card (Fixed Top) -->
    <q-card flat bordered class="bom-summary-card">
      <q-card-section class="bg-primary text-white q-pa-sm">
        <div class="text-subtitle2 text-weight-bold">BOM Summary</div>
      </q-card-section>

      <q-separator />

      <q-card-section v-if="loading" class="q-pa-md text-center">
        <q-spinner color="primary" size="40px" />
        <div class="text-caption text-grey-7 q-mt-sm">Loading BOM data...</div>
      </q-card-section>

      <q-card-section v-else-if="error" class="q-pa-sm">
        <q-banner dense class="bg-negative text-white" rounded>
          <template #avatar>
            <q-icon name="error" />
          </template>
          {{ error }}
        </q-banner>
      </q-card-section>

      <q-card-section v-else class="q-pa-sm">
        <div class="text-body2">
          <!-- Quote Info -->
          <div class="q-mb-sm text-weight-medium">
            {{ bomData.quote_number }}
            <q-badge v-if="bomData.option_name" color="grey-7" outline class="q-ml-xs">
              {{ bomData.option_name }}
            </q-badge>
          </div>

          <q-separator class="q-my-sm" />

          <!-- Device Stats -->
          <div class="stat-row">
            <q-icon name="precision_manufacturing" size="18px" class="q-mr-xs text-grey-7" />
            <span class="text-grey-8">Total Devices:</span>
            <span class="text-weight-bold q-ml-auto">{{ totals.total_devices }}</span>
          </div>
          <div class="stat-row q-ml-md">
            <q-icon name="check_circle" size="16px" class="q-mr-xs text-positive" />
            <span class="text-grey-7">With Parts:</span>
            <span class="q-ml-auto">{{ totals.devices_with_parts }}</span>
          </div>
          <div class="stat-row q-ml-md">
            <q-icon name="warning" size="16px" class="q-mr-xs text-warning" />
            <span class="text-grey-7">Without Parts:</span>
            <span class="q-ml-auto">{{ totals.devices_without_parts }}</span>
          </div>

          <q-separator class="q-my-sm" />

          <!-- Parts Stats -->
          <div class="stat-row">
            <q-icon name="inventory_2" size="18px" class="q-mr-xs text-grey-7" />
            <span class="text-grey-8">Parts Needed:</span>
            <span class="text-weight-bold q-ml-auto">{{ totals.unique_parts_needed }}</span>
          </div>
          <div class="stat-row q-ml-md">
            <q-icon name="check" size="16px" class="q-mr-xs text-positive" />
            <span class="text-grey-7">Matched:</span>
            <span class="q-ml-auto">{{ totals.matched_parts }}</span>
          </div>
          <div class="stat-row q-ml-md">
            <q-icon name="priority_high" size="16px" class="q-mr-xs text-warning" />
            <span class="text-grey-7">Shortages:</span>
            <span class="q-ml-auto">{{ totals.parts_with_shortages }}</span>
          </div>
          <div class="stat-row q-ml-md">
            <q-icon name="add_circle" size="16px" class="q-mr-xs text-orange" />
            <span class="text-grey-7">Excess:</span>
            <span class="q-ml-auto">{{ totals.excess_parts }}</span>
          </div>
        </div>
      </q-card-section>
    </q-card>

    <!-- Scrollable Details Section -->
    <div class="col overflow-auto q-mt-sm">
      <q-list v-if="!loading && !error" bordered separator>
        <!-- Matched Parts -->
        <q-expansion-item
          :label="`Matched Parts (${bomData.matched?.length || 0})`"
          icon="check_circle"
          header-class="bg-positive text-white"
          expand-icon-class="text-white"
          :default-opened="false"
        >
          <q-card>
            <q-card-section v-if="bomData.matched?.length === 0" class="text-grey-7 text-center">
              No matched parts
            </q-card-section>
            <q-list v-else dense separator>
              <q-item v-for="(part, index) in bomData.matched" :key="`matched-${index}`" dense>
                <q-item-section>
                  <q-item-label class="text-weight-medium">{{ part.part_number }}</q-item-label>
                  <q-item-label caption>{{ part.description }}</q-item-label>
                  <q-item-label caption class="text-grey-6">
                    {{ part.manufacturer }} {{ part.model }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge color="positive" outline>
                    {{ part.needed }}
                  </q-badge>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </q-expansion-item>

        <!-- Parts with Shortages -->
        <q-expansion-item
          :label="`Parts with Shortages (${bomData.shortages?.length || 0})`"
          icon="warning"
          header-class="bg-warning text-white"
          expand-icon-class="text-white"
          :default-opened="bomData.shortages?.length > 0"
        >
          <q-card>
            <q-card-section v-if="bomData.shortages?.length === 0" class="text-grey-7 text-center">
              No shortages
            </q-card-section>
            <q-list v-else dense separator>
              <q-item v-for="(part, index) in bomData.shortages" :key="`shortage-${index}`" dense>
                <q-item-section>
                  <q-item-label class="text-weight-medium">{{ part.part_number }}</q-item-label>
                  <q-item-label caption>{{ part.description }}</q-item-label>
                  <q-item-label caption class="text-grey-6">
                    {{ part.manufacturer }} {{ part.model }}
                  </q-item-label>
                  <q-item-label caption class="text-warning">
                    Need {{ part.needed }}, Quoted {{ part.quoted }}, Short {{ part.shortage }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge color="warning">
                    -{{ part.shortage }}
                  </q-badge>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </q-expansion-item>

        <!-- Excess Parts -->
        <q-expansion-item
          :label="`Excess Parts (${bomData.excess?.length || 0})`"
          icon="add_circle"
          header-class="bg-orange text-white"
          expand-icon-class="text-white"
          :default-opened="false"
        >
          <q-card>
            <q-card-section v-if="bomData.excess?.length === 0" class="text-grey-7 text-center">
              No excess parts
            </q-card-section>
            <q-list v-else dense separator>
              <q-item v-for="(part, index) in bomData.excess" :key="`excess-${index}`" dense>
                <q-item-section>
                  <q-item-label class="text-weight-medium">
                    {{ part.quote_sources?.[0]?.product_name || part.description || part.part_number }}
                  </q-item-label>
                  <q-item-label caption>Part: {{ part.part_number }}</q-item-label>
                  <q-item-label caption class="text-orange">
                    Quoted {{ part.quoted_qty }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge color="orange">
                    +{{ part.excess_qty }}
                  </q-badge>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </q-expansion-item>

        <!-- By Others (Supply Exclusions) - collapsed by default as we don't need to action these -->
        <q-expansion-item
          :label="`By Others (${bomData.by_others?.length || 0})`"
          icon="person_off"
          header-class="bg-grey-6 text-white"
          expand-icon-class="text-white"
          :default-opened="false"
        >
          <q-card>
            <q-card-section v-if="bomData.by_others?.length === 0" class="text-grey-7 text-center">
              No items excluded from scope
            </q-card-section>
            <q-list v-else dense separator>
              <q-item v-for="(item, index) in bomData.by_others" :key="`by-others-${index}`" dense>
                <q-item-section>
                  <q-item-label class="text-weight-medium">{{ item.device_name || item.part_name }}</q-item-label>
                  <q-item-label caption class="text-grey-6">
                    {{ formatBrickClass(item.brick_class) }}
                  </q-item-label>
                  <q-item-label caption>
                    <q-badge color="grey-6" outline size="sm">
                      {{ item.exclusion_reason }}
                    </q-badge>
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    flat
                    round
                    dense
                    icon="add_circle"
                    color="positive"
                    size="sm"
                    @click="includeInScope(item)"
                  >
                    <q-tooltip>Include in scope</q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </q-expansion-item>

        <!-- Unassigned Devices -->
        <q-expansion-item
          :label="`Unassigned Devices (${bomData.unassigned_devices?.length || 0})`"
          icon="error"
          header-class="bg-negative text-white"
          expand-icon-class="text-white"
          :default-opened="bomData.unassigned_devices?.length > 0"
        >
          <q-card>
            <q-card-section v-if="bomData.unassigned_devices?.length === 0" class="text-grey-7 text-center">
              All devices have parts assigned
            </q-card-section>
            <q-list v-else dense separator>
              <q-item
                v-for="device in groupedUnassignedDevices"
                :key="`unassigned-${device.id}`"
                clickable
                dense
                @click="handleDeviceClick(device)"
              >
                <q-item-section>
                  <q-item-label class="text-weight-medium">
                    {{ device.name }}
                    <q-badge v-if="device.count > 1" color="grey-7" outline class="q-ml-xs">
                      {{ device.count }}
                    </q-badge>
                  </q-item-label>
                  <q-item-label caption>{{ device.brick_class }}</q-item-label>
                  <q-item-label caption class="text-grey-6">
                    In: {{ device.equipment_names_display }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-icon name="chevron_right" color="grey-5" />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </q-expansion-item>
      </q-list>
    </div>

    <!-- Action Buttons (Fixed Bottom) -->
    <div class="bom-actions q-pt-sm q-pb-sm">
      <q-btn
        outline
        color="primary"
        icon="refresh"
        label="Refresh BOM"
        class="full-width q-mb-xs"
        size="sm"
        :loading="loading"
        @click="refreshBom"
      />
      <q-btn
        outline
        color="secondary"
        icon="download"
        label="Export BOM"
        class="full-width"
        size="sm"
        :disable="loading || error"
        @click="exportBom"
      />
    </div>

    <!-- BOM Management Dialog -->
    <quote-bom-management-dialog
      v-model="showManagementDialog"
      :quote-id="quoteId"
      :bom-data="bomData"
      @refresh="fetchBomData"
    />
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import { quoteService } from '@/services/quoteService';
  import { api } from '@/services/api';
  import { formatBrickClass } from '@/composables/brick-schema/operations/useBrickValidation.js';
  import QuoteBomManagementDialog from './QuoteBomManagementDialog.vue';

  const props = defineProps({
    quoteId: {
      type: Number,
      required: true,
    },
    refreshTrigger: {
      type: Number,
      default: 0,
    },
  });

  const emit = defineEmits(['device-selected']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const error = ref(null);
  const showManagementDialog = ref(false);
  const bomData = ref({
    quote_id: null,
    quote_number: '',
    option_name: '',
    status: '',
    is_locked: false,
    matched: [],
    shortages: [],
    excess: [],
    by_others: [],
    unassigned_devices: [],
    totals: {
      total_devices: 0,
      devices_with_parts: 0,
      devices_without_parts: 0,
      unique_parts_needed: 0,
      unique_parts_quoted: 0,
      matched_parts: 0,
      parts_with_shortages: 0,
      excess_parts: 0,
      by_others_count: 0,
    },
  });

  // Computed
  const totals = computed(() => bomData.value.totals || {});

  /**
   * Group unassigned devices by name and brick_class
   * Returns an array of grouped devices with count and comma-separated equipment names
   */
  const groupedUnassignedDevices = computed(() => {
    if (!bomData.value.unassigned_devices?.length) {
      return [];
    }

    // Group by name + brick_class combination
    const groups = {};

    bomData.value.unassigned_devices.forEach((device) => {
      const key = `${device.name}|||${device.brick_class}`;

      if (!groups[key]) {
        groups[key] = {
          name: device.name,
          brick_class: device.brick_class,
          equipment_names: [],
          device_ids: [],
          count: 0,
        };
      }

      groups[key].equipment_names.push(device.equipment_name);
      groups[key].device_ids.push(device.id);
      groups[key].count++;
    });

    // Convert to array and format equipment names
    return Object.values(groups).map((group) => ({
      name: group.name,
      brick_class: group.brick_class,
      equipment_names_display: group.equipment_names.join(', '),
      device_ids: group.device_ids,
      count: group.count,
      // For click handler - use first device ID
      id: group.device_ids[0],
    }));
  });

  // Methods
  async function fetchBomData() {
    if (!props.quoteId) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await quoteService.getBomReconciliation(props.quoteId);
      bomData.value = response.data || response;
    } catch (err) {
      console.error('Failed to fetch BOM data:', err);
      error.value = err.message || 'Failed to load BOM data';
      $q.notify({
        type: 'negative',
        message: 'Failed to load BOM data',
        caption: err.message,
      });
    } finally {
      loading.value = false;
    }
  }

  function refreshBom() {
    fetchBomData();
  }

  function exportBom() {
    // Generate CSV from BOM data
    const csv = generateBomCsv();

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BOM-${bomData.value.quote_number}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    $q.notify({
      type: 'positive',
      message: 'BOM exported successfully',
    });
  }

  function generateBomCsv() {
    const lines = [];

    // Header
    lines.push('BOM Reconciliation Report');
    lines.push(`Quote: ${bomData.value.quote_number}`);
    if (bomData.value.option_name) {
      lines.push(`Option: ${bomData.value.option_name}`);
    }
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');

    // Summary
    lines.push('SUMMARY');
    lines.push(`Total Devices,${totals.value.total_devices}`);
    lines.push(`Devices with Parts,${totals.value.devices_with_parts}`);
    lines.push(`Devices without Parts,${totals.value.devices_without_parts}`);
    lines.push(`Unique Parts Needed,${totals.value.unique_parts_needed}`);
    lines.push(`Matched Parts,${totals.value.matched_parts}`);
    lines.push(`Parts with Shortages,${totals.value.parts_with_shortages}`);
    lines.push(`Excess Parts,${totals.value.excess_parts}`);
    lines.push('');

    // Matched Parts
    if (bomData.value.matched?.length > 0) {
      lines.push('MATCHED PARTS');
      lines.push('Part Number,Description,Manufacturer,Model,Quantity');
      bomData.value.matched.forEach((part) => {
        lines.push(`"${part.part_number}","${part.description}","${part.manufacturer}","${part.model}",${part.needed}`);
      });
      lines.push('');
    }

    // Shortages
    if (bomData.value.shortages?.length > 0) {
      lines.push('PARTS WITH SHORTAGES');
      lines.push('Part Number,Description,Manufacturer,Model,Needed,Quoted,Shortage');
      bomData.value.shortages.forEach((part) => {
        lines.push(`"${part.part_number}","${part.description}","${part.manufacturer}","${part.model}",${part.needed},${part.quoted},${part.shortage}`);
      });
      lines.push('');
    }

    // Excess
    if (bomData.value.excess?.length > 0) {
      lines.push('EXCESS PARTS');
      lines.push('Part Number,Description,Quoted,Excess');
      bomData.value.excess.forEach((part) => {
        lines.push(`"${part.part_number}","${part.description || ''}",${part.quoted},${part.excess}`);
      });
      lines.push('');
    }

    // Unassigned Devices
    if (bomData.value.unassigned_devices?.length > 0) {
      lines.push('UNASSIGNED DEVICES');
      lines.push('Device Name,BRICK Class,Count,Equipment');
      groupedUnassignedDevices.value.forEach((device) => {
        lines.push(`"${device.name}","${device.brick_class}",${device.count},"${device.equipment_names_display}"`);
      });
    }

    return lines.join('\n');
  }

  function handleDeviceClick(device) {
    emit('device-selected', device.id);
    $q.notify({
      type: 'info',
      message: `Navigating to device: ${device.name}`,
      timeout: 1000,
    });
  }

  /**
   * Include an excluded item in scope by creating an override rule
   */
  async function includeInScope(item) {
    try {
      // Create a quote-specific override rule to include this item
      const ruleData = {
        target_type: item.brick_class ? 'brick_class' : 'device_part',
        target_value: item.brick_class || item.part_number,
        reason: 'Included per quote requirement',
        is_excluded: false, // This is an override (include rule)
      };

      await api.post(`/quotes/${props.quoteId}/supply-rules`, ruleData);

      $q.notify({
        type: 'positive',
        message: `"${item.device_name || item.part_name}" will now be included in scope`,
      });

      // Refresh BOM data to reflect the change
      fetchBomData();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to include item in scope: ' + error.message,
      });
    }
  }

  // Watchers
  watch(() => props.refreshTrigger, () => {
    if (props.refreshTrigger > 0) {
      fetchBomData();
    }
  });

  // Lifecycle
  onMounted(() => {
    fetchBomData();
  });
</script>

<style scoped>
.bom-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.bom-summary-card {
  flex-shrink: 0;
}

.bom-actions {
  flex-shrink: 0;
}

.stat-row {
  display: flex;
  align-items: center;
  padding: 2px 0;
  font-size: 0.875rem;
}

.stat-row .q-icon {
  flex-shrink: 0;
}

.expand-trigger {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  background: #1976d2;
  color: white;
  padding: 6px 2px;
  border-radius: 0 3px 3px 0;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transition: all 0.3s;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 18px;
}

.expand-trigger:hover {
  background: #1565c0;
  width: 22px;
}

.expand-text {
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
</style>
